import axios, { AxiosInstance, Method, AxiosRequestConfig } from 'axios';
import {
  getAuthData,
  isLoginAsSession,
  preserveMainAuthForLoginAs,
  setAuthTokens,
} from './authData';
import {
  assertAuthStateMatchesRefreshedAccessToken,
  clearAuthAndRedirectAfterRefreshFailure,
  clearAuthAndRedirectToSignIn,
  getAuthSessionIdentity,
  getLatestAccessTokenAfterAuthChange,
  hasAuthSessionIdentityChanged,
  refreshAccessToken,
  resolveLoginAsRequestAuthFailure,
  shouldSkipRefresh,
} from './authRefresh';
import { getEnvironmentConfig } from './environment';
import { createCorrelationRequestHeaders } from './observability/request-headers';

const { apiUrl } = getEnvironmentConfig();
export const API_URL = apiUrl;

export interface AxiosResponseData {
  [key: string]: any;
}

interface IAuthRetryAxiosRequestConfig extends AxiosRequestConfig {
  _authRetry?: boolean;
  _accessToken?: string | null;
  _authSessionIdentity?: string | null;
  _loginAsRequest?: boolean;
}

export const instance: AxiosInstance = axios.create({
  baseURL: API_URL,
  responseType: 'json',
});

/* request interceptor */
instance.interceptors.request.use((request) => {
  const authRequest = request as IAuthRetryAxiosRequestConfig;
  const accessToken = getAuthData().Authorization;
  authRequest._accessToken = accessToken ?? null;
  authRequest._authSessionIdentity = getAuthSessionIdentity(accessToken ?? null);
  authRequest._loginAsRequest = isLoginAsSession();

  if (!request.url?.includes('https://') && !shouldSkipRefresh(request)) {
    Object.assign(request.headers, getAuthData(), createCorrelationRequestHeaders());
  }

  return request;
});

/* response interceptor */
instance.interceptors.response.use(
  (config) => {
    if (config.data?.accessToken) {
      if (config.config.url === '/auth/one-time-entry') {
        preserveMainAuthForLoginAs();
        localStorage.setItem('isSuperAdminLogged', 'true');
      }
      setAuthTokens({
        accessToken: config.data.accessToken,
        refreshToken:
          config.config.url === '/auth/one-time-entry' ? undefined : config.data.refreshToken,
      });
    }

    return config.data;
  },
  async (responseData = {}) => {
    const { response, message } = responseData;
    const data = response?.data;
    const status = response?.status;
    const originalRequest = responseData.config as IAuthRetryAxiosRequestConfig | undefined;

    if (status !== 401 || !originalRequest || shouldSkipRefresh(originalRequest)) {
      return Promise.reject({ data, message, status });
    }

    if (originalRequest._authRetry) {
      if (hasAuthSessionIdentityChanged(originalRequest._authSessionIdentity ?? null)) {
        return Promise.reject({ data, message, status });
      }

      clearAuthAndRedirectToSignIn();
      return Promise.reject({ data, message, status });
    }

    if (originalRequest._loginAsRequest) {
      resolveLoginAsRequestAuthFailure();
      return Promise.reject({ data, message, status });
    }

    if (hasAuthSessionIdentityChanged(originalRequest._authSessionIdentity ?? null)) {
      return Promise.reject({ data, message, status });
    }

    const latestAccessToken = getLatestAccessTokenAfterAuthChange(
      originalRequest._accessToken ?? null
    );
    if (latestAccessToken) {
      originalRequest._authRetry = true;
      originalRequest.headers = {
        ...originalRequest.headers,
        Authorization: latestAccessToken,
      };

      return instance(originalRequest);
    }

    try {
      const accessToken = await refreshAccessToken();
      assertAuthStateMatchesRefreshedAccessToken(accessToken);
      originalRequest._authRetry = true;
      originalRequest.headers = {
        ...originalRequest.headers,
        Authorization: accessToken,
      };

      return instance(originalRequest);
    } catch (error) {
      clearAuthAndRedirectAfterRefreshFailure(error);
    }

    return Promise.reject({ data, message, status });
  }
);

type URL = string | ((id?: number | string) => string);

interface IArg extends AxiosRequestConfig {
  id?: number | string;
}

export type GetAxiosResponse = <T = AxiosResponseData>(args?: IArg) => Promise<T>;

export type TMakeAxiosRequest = (url: URL, method: Method) => GetAxiosResponse;

const makeAxiosRequest: TMakeAxiosRequest =
  (url, method) =>
  (args = {}) => {
    const { id } = args;
    return instance({
      url: typeof url === 'function' ? url(id) : url,
      method,
      ...args,
    }) as Promise<any>;
  };

export default makeAxiosRequest;
